/**
 * Represents a BottomSheet component that can be shown, hidden, and resized interactively.
 * Enlace al confluence:
 * https://thecocktail.atlassian.net/wiki/spaces/IBE/pages/4270293015/Bottom+Sheet
 */
import { currentDevice } from '../../core/utils/currentDevice'
import on from '../../utils/events_delegation'
import { vhToPx } from '../../utils/unit_conversion'

window.bsInstances = []

export class BottomSheet {
  /**
   * @param {Object} config - Configuration object for the BottomSheet.
   * @param {HTMLElement} config.showButton - The button that triggers the BottomSheet to open.
   * @param {String} config.sheetId - The id of the BottomSheet to open.
   * @param {number|string} [config.sheetHeight='auto'] - Default height in 'auto' or a specific value in vh.
   * @throws {Error} Throws an error if `showButton` is not provided.
   * @throws {Error} Throws an error if the `sheetSelector` dataset is not found on `showButton`.
   */

  static instances = []
  static scrollLockRefCount = 0

  constructor(config = {}) {
    if (!config.showButton && !config.sheetId) {
      throw new Error(
        "The configuration is missing a required property. Please provide either 'showButton' or 'sheetId'."
      )
    }

    // Set / parse sheetHeight
    if (config.sheetHeight === undefined || config.sheetHeight === 'auto') {
      config.sheetHeight = 'auto'
    } else {
      const parsedHeight = parseInt(config.sheetHeight, 10)
      if (!isNaN(parsedHeight)) {
        config.sheetHeight = parsedHeight
      }
    }

    if (config.showButton) {
      this.showBtn = config.showButton
      this.sheetId = config.sheetId || this.showBtn.dataset.sheetSelector
      this.showBtn.setAttribute('aria-haspopup', 'true')
      this.showBtn.setAttribute('aria-expanded', 'false')
    }

    if (config.showButton && !this.sheetId && !config.sheetId) {
      throw new Error("The 'sheetSelector' dataset in 'showButton' is required.")
    }

    this.config = config

    if (config.sheetId) {
      this.sheetId = config.sheetId
    }

    this.bottomSheet = document.querySelector(`#${this.sheetId}`)

    if (!this.bottomSheet) {
      throw new Error(`BottomSheet element with id '${this.sheetId}' not found in the DOM.`)
    }

    this.sheetOverlay = this.bottomSheet.querySelector('.js-sheet-overlay')
    this.sheetContent = this.bottomSheet.querySelector('.js-sheet-content')
    this.sheetBody = this.bottomSheet.querySelector('.js-sheet-body')
    this.sheetFooter = this.bottomSheet.querySelector('.js-sheet-footer')
    this.sheetFooterCtas = this.bottomSheet.querySelector('.js-sheet-footer-ctas')
    this.dragIcon = this.bottomSheet.querySelector('.js-drag-icon')

    this.isDragging = false
    this.eventListeners = {} // Object to store event listeners

    // A11y
    this.bottomSheet.setAttribute('role', 'dialog')
    this.bottomSheet.setAttribute('aria-modal', 'true')
    this.bottomSheet.setAttribute('tabindex', '-1')
    this.bottomSheet.inert = true

    if (config.testSelector) {
      this.bottomSheet.dataset.test = config.testSelector
    }

    // Append bottom sheet to body before show to ensure correct positioning
    if (this.bottomSheet.parentNode !== document.body) {
      document.body.appendChild(this.bottomSheet)
    }

    // State
    this.initialSheetHeight = null
    this.sheetHeight = null
    this.triggeringElementOnOpen = null
    this._isTransitioning = false

    // Bind handlers once
    this._onDragStart = this.dragStart.bind(this)
    this._onSheetKeyDown = e => {
      if (e.key === 'Escape') this.hide()
      this.trapTabKey(e)
    }
    this._onShowBtnClick = () => this.show(this.showBtn)

    this._onDocumentFocusIn = e => {
      /*Only the most top sheet has to capture the focus*/
      if (!this.isVisible()) return
      if (BottomSheet.getTopVisibleInstance() !== this) return

      const tgt = e.target
      if (tgt && tgt.closest && tgt.closest('.js-ibh-bottom-sheet')) {
        return
      }

      if (!this.bottomSheet.contains(e.target)) {
        this.focusFirst()
      }
    }

    this._onResize = () => this.calcInitialSheetHeight()

    // Initial height setup
    this.calcInitialSheetHeight()
    if (window.visualViewport) {
      this._onViewportResize = () => {
        if (this.isVisible()) {
          this.calcInitialSheetHeight()
        }
      }
      window.visualViewport.addEventListener('resize', this._onViewportResize)
    }
    this.init()

    BottomSheet.instances.push(this)
    window.bsInstances.push(this)
  }

  // ---------- Static helpers (stacking) ----------
  static getVisibleInstances() {
    return BottomSheet.instances.filter(i => {
      if (!i || !i.bottomSheet) return false
      if (!document.body.contains(i.bottomSheet)) return false
      return i.isVisible()
    })
  }

  static getTopVisibleInstance() {
    const visibles = BottomSheet.getVisibleInstances()
    if (!visibles.length) return null

    return visibles[visibles.length - 1]
  }

  // ---------- Focus management ----------
  getFocusableElements() {
    if (!this.bottomSheet) return []
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

    return [...this.bottomSheet.querySelectorAll(selector)]
      .filter(el => !el.closest('[inert]'))
      .filter(el => el.offsetParent !== null)
      .filter(el => getComputedStyle(el).visibility !== 'hidden')
  }

  focusFirst() {
    if (!this.bottomSheet) return
    const focusables = this.getFocusableElements()
    if (focusables.length) {
      focusables[0].focus({ preventScroll: true })
    } else {
      this.bottomSheet.focus({ preventScroll: true })
    }
  }

  trapTabKey(e) {
    if (e.key !== 'Tab') return
    if (BottomSheet.getTopVisibleInstance() !== this) return
    if (!this.bottomSheet) return

    const focusables = this.getFocusableElements()
    if (!focusables.length) {
      e.preventDefault()
      this.bottomSheet.focus({ preventScroll: true })
      return
    }

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement

    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus({ preventScroll: true })
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus({ preventScroll: true })
    }
  }

  getBottomSheetElem() {
    return this.bottomSheet
  }

  calcInitialSheetHeight() {
    // Initial height setup
    if (!this.sheetContent || !this.bottomSheet) {
      return Promise.resolve()
    }

    this.sheetContent.style.height = 'auto'

    if (this.sheetBody && this.sheetFooterCtas) {
      const footerCtasHeight = this.sheetFooterCtas.clientHeight
      this.sheetBody.style.paddingBottom = `${footerCtasHeight}px`
    }

    return new Promise(resolve => {
      requestAnimationFrame(() => {
        if (!this.bottomSheet || !this.sheetContent) {
          resolve()
          return
        }

        const maxHeight = this.getWindowMaxHeight()

        let newHeight
        if (this.config.sheetHeight === 'auto') {
          const headerHeight = this.bottomSheet.querySelector('.bottom-sheet__header')?.clientHeight || 0
          const bodyHeight = this.sheetBody?.scrollHeight || 0
          const footerElement = this.sheetFooter
          const footerHeight = footerElement ? footerElement.scrollHeight : 0
          const contentHeight = headerHeight + bodyHeight + footerHeight

          if (contentHeight > maxHeight) {
            newHeight = maxHeight
          } else {
            newHeight = contentHeight
          }

          if (this.initialSheetHeight) {
            newHeight = Math.max(this.initialSheetHeight, newHeight)
          }
        } else {
          newHeight = vhToPx(this.config.sheetHeight)
        }

        if (!this.initialSheetHeight) {
          this.initialSheetHeight = newHeight
        }

        this.sheetHeight = newHeight
        this.updateSheetHeight(this.sheetHeight)
        resolve()
      })
    })
  }

  getWindowMaxHeight() {
    return window.visualViewport?.height ?? window.innerHeight
  }

  /**
   * Registers a listener for a specific event.
   * @param {string} eventName The name of the event to listen for.
   * @param {Function} callback The function to be called when the event is triggered.
   */
  addEventListener(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = []
    }
    this.eventListeners[eventName].push(callback)
  }

  /**
   * Removes a listener for a specific event.
   * @param {string} eventName The name of the event.
   * @param {Function} callback The callback function to remove.  Must be the same function that was registered.
   */
  removeEventListener(eventName, callback) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName] = this.eventListeners[eventName].filter(listener => listener !== callback)
    }
  }

  /**
   * Triggers/emits a specific event.
   * @param {string} eventName The name of the event to trigger.
   */
  triggerEvent(eventName) {
    if (this.eventListeners[eventName]) {
      this.eventListeners[eventName].forEach(listener => listener(this.bottomSheet))
    }
  }

  /**
   * Appends a new element to the end of the body if the given element is NOT the last element with the class 'bottom-sheet'
   */
  setAsLastInBody() {
    return new Promise(resolve => {
      const bottomSheetElements = document.querySelectorAll('.bottom-sheet')

      const isLast = this.bottomSheet === bottomSheetElements[bottomSheetElements.length - 1]

      if (!isLast) {
        document.body.appendChild(this.bottomSheet)
        resolve()
      } else {
        resolve()
      }
    })
  }

  /**
   * Shows the BottomSheet and disables body scrolling.
   */
  async show(triggeringElement = null, callback) {
    if (!this.bottomSheet || this.isVisible() || this._isTransitioning) return
    this._isTransitioning = true

    await this.setAsLastInBody()
    this.triggeringElementOnOpen = triggeringElement || this.showBtn || document.activeElement

    BottomSheet.instances.forEach(inst => {
      if (inst !== this && inst.isVisible() && inst.bottomSheet) inst.bottomSheet.inert = true
    })

    BottomSheet.scrollLockRefCount++
    if (BottomSheet.scrollLockRefCount === 1) {
      ScrollLock.on()
    }

    // Force a repaint before adding the animation class
    requestAnimationFrame(() => {
      requestAnimationFrame(async () => {
        // Double requestAnimationFrame
        if (!this.bottomSheet) {
          this._isTransitioning = false
          return
        }
        this.bottomSheet.classList.remove('is-hidden')

        await this.calcInitialSheetHeight()

        if (!this.bottomSheet) {
          this._isTransitioning = false
          return
        }
        this.bottomSheet.classList.add('is-visible')

        if (this.showBtn) {
          this.showBtn.setAttribute('aria-expanded', 'true')
        }

        this.bottomSheet.inert = false

        document.addEventListener('focusin', this._onDocumentFocusIn, true)

        this.focusFirst()

        this._isTransitioning = false
      })
    })

    this.updateSheetHeight(this.sheetHeight)

    this.triggerEvent('show')
    // Handle callback if it was passed as the first argument (due to old signature)
    if (typeof triggeringElement === 'function' && callback === undefined) {
      triggeringElement(this.bottomSheet)
    } else if (callback && typeof callback === 'function') {
      callback(this.bottomSheet)
    }
  }

  /**
   * Updates the height of the BottomSheet content.
   * @param {number} height - The new height of the BottomSheet.
   */
  updateSheetHeight(height) {
    if (!this.bottomSheet || !this.sheetContent) return
    const minHeight = 100
    const maxHeight = this.getWindowMaxHeight()
    const validatedHeight = Math.min(Math.max(height, minHeight), maxHeight)

    this.sheetContent.style.height = `${validatedHeight}px`
    this.bottomSheet.classList.toggle('is-fullscreen', validatedHeight === maxHeight)
  }

  /**
   * Hides the BottomSheet and restores body scrolling.
   */
  hide() {
    if (!this.isVisible() || this._isTransitioning) return
    if (!this.bottomSheet) return
    this._isTransitioning = true

    this.triggerEvent('close')

    const toFocus = this.triggeringElementOnOpen
    this.triggeringElementOnOpen = null

    this.bottomSheet.classList.remove('is-visible')
    this.bottomSheet.inert = true

    if (this.showBtn) {
      this.showBtn.setAttribute('aria-expanded', 'false')
    }

    // Reset height to initial value
    this.updateSheetHeight(this.initialSheetHeight)

    // delete focus guard
    document.removeEventListener('focusin', this._onDocumentFocusIn, true)

    BottomSheet.scrollLockRefCount = Math.max(0, BottomSheet.scrollLockRefCount - 1)
    if (BottomSheet.scrollLockRefCount === 0) {
      ScrollLock.off()
    }

    const prevVisible = [...BottomSheet.instances].reverse().find(inst => inst !== this && inst.isVisible())
    if (prevVisible && prevVisible.bottomSheet) {
      prevVisible.bottomSheet.inert = false
    }

    if (toFocus && typeof toFocus.focus === 'function') {
      setTimeout(() => toFocus.focus({ preventScroll: true }), 0)
    }

    setTimeout(() => {
      if (this.bottomSheet) {
        this.bottomSheet.classList.add('is-hidden')
        this._isTransitioning = false
      }
    }, 300)

    this.removeDragListeners()
  }

  /**
   * Starts the drag operation to resize the BottomSheet.
   * @param {MouseEvent|TouchEvent} e - The drag start event.
   */
  dragStart(e) {
    e.preventDefault()
    e.stopPropagation()

    if (!e.target?.closest('.js-drag-icon')) return
    if (!this.bottomSheet || !this.sheetContent) return

    this.isDragging = true
    this.startY = e.pageY || e.touches?.[0].pageY

    const computedHeight = window.getComputedStyle(this.sheetContent).height
    this.startHeight = parseInt(computedHeight, 10)

    if (isNaN(this.startHeight)) {
      this.startHeight = this.sheetContent.clientHeight
    }
    if (isNaN(this.startHeight)) {
      this.isDragging = false
      return
    }

    this.bottomSheet.classList.add('is-dragging')

    this.draggingBound = this.dragging.bind(this)
    this.dragStopBound = this.dragStop.bind(this)

    document.addEventListener('mousemove', this.draggingBound)
    document.addEventListener('mouseup', this.dragStopBound)
    document.addEventListener('touchmove', this.draggingBound, { passive: false })
    document.addEventListener('touchend', this.dragStopBound)
    document.addEventListener('touchcancel', this.dragStopBound)
  }

  /**
   * Handles the drag operation and adjusts the BottomSheet height dynamically.
   * @param {MouseEvent|TouchEvent} e - The drag event.
   */
  dragging(e) {
    e.preventDefault()
    e.stopPropagation()

    if (!this.isDragging) return

    const delta = this.startY - (e.pageY || e.touches?.[0].pageY)
    const newHeight = this.startHeight + delta
    this.updateSheetHeight(newHeight)
  }

  /**
   * Ends the drag operation and determines the final state of the BottomSheet.
   */
  dragStop() {
    if (!this.isDragging || !this.bottomSheet || !this.sheetContent) return

    this.isDragging = false
    this.bottomSheet.classList.remove('is-dragging')

    const computedHeight = window.getComputedStyle(this.sheetContent).height
    const sheetHeight = parseInt(computedHeight, 10)

    if (isNaN(sheetHeight)) {
      this.removeDragListeners()
      return
    }

    if (sheetHeight < this.startHeight) {
      if (sheetHeight <= this.initialSheetHeight * 0.75) {
        this.hide()
      } else {
        this.updateSheetHeight(this.initialSheetHeight)
      }
    } else {
      const maxHeight = this.getWindowMaxHeight()
      this.updateSheetHeight(maxHeight)
    }

    this.removeDragListeners()
  }

  /**
   * Removes drag event listeners.
   */
  removeDragListeners() {
    document.removeEventListener('mousemove', this.draggingBound)
    document.removeEventListener('mouseup', this.dragStopBound)
    document.removeEventListener('touchmove', this.draggingBound)
    document.removeEventListener('touchend', this.dragStopBound)
    document.removeEventListener('touchcancel', this.dragStopBound)
  }

  /**
   * Adds all necessary event listeners to the BottomSheet and related elements.
   */
  addEventListeners() {
    this.dragIcon?.addEventListener('mousedown', this._onDragStart)
    this.dragIcon?.addEventListener('touchstart', this._onDragStart, { passive: false })

    this._onSheetClick = e => {
      if (!this.isVisible()) return
      if (this.sheetContent && !this.sheetContent.contains(e.target)) {
        this.hide()
      }
    }
    this.bottomSheet?.addEventListener('click', this._onSheetClick)

    if (this.showBtn) {
      this.showBtn.addEventListener('click', this._onShowBtnClick)
    }

    window.addEventListener('resize', this._onResize)

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this._onResize)
    }

    this.bottomSheet.addEventListener('keydown', this._onSheetKeyDown)

    on({
      eventName: 'click',
      selector: `#${this.sheetId} [data-bottom-sheet-close]`,
      handler: event => {
        const targetSheet = event.target.closest('.bottom-sheet')
        if (targetSheet === this.bottomSheet) this.hide()
      },
    })
  }

  /**
   * Checks if the BottomSheet is currently visible.
   * @returns {boolean} True if the BottomSheet is visible, false otherwise.
   */
  isVisible() {
    if (!this.bottomSheet || !document.body.contains(this.bottomSheet)) {
      this.bottomSheet = document.querySelector(`#${this.sheetId}`)
    }

    if (!this.bottomSheet) {
      return false
    }

    return this.bottomSheet.classList.contains('is-visible')
  }

  /**
   * Closes the BottomSheet programmatically.
   */
  close() {
    this.hide()
  }

  /**
   * Closes all BottomSheet programmatically.
   */
  static closeAll() {
    BottomSheet.instances.forEach(instance => {
      if (!instance.bottomSheet) return
      instance.close()
    })
  }

  /**
   * Initializes the BottomSheet by adding event listeners.
   */
  init() {
    this.addEventListeners()
    this._heightUpdateTimeout = null

    this._observer = new MutationObserver(() => {
      if (this._heightUpdateTimeout) {
        clearTimeout(this._heightUpdateTimeout)
      }
      this._heightUpdateTimeout = setTimeout(() => {
        const newHeight = this.sheetContent.clientHeight
        if (newHeight !== this.sheetHeight) {
          this.sheetHeight = newHeight
          this.updateSheetHeight(this.sheetHeight)
        }
      }, 100)
    })
    this._observer.observe(this.sheetContent, { childList: true, subtree: false })
  }

  /**
   * Removes all event listeners and clears references to DOM elements.
   */
  destroy() {
    if (this.isVisible()) {
      BottomSheet.scrollLockRefCount = Math.max(0, BottomSheet.scrollLockRefCount - 1)
      if (BottomSheet.scrollLockRefCount === 0) {
        ScrollLock.off()
      }
    }

    this.dragIcon?.removeEventListener('mousedown', this._onDragStart)
    this.dragIcon?.removeEventListener('touchstart', this._onDragStart)

    this.bottomSheet?.removeEventListener('click', this._onSheetClick)

    this.showBtn?.removeEventListener('click', this._onShowBtnClick)

    if (currentDevice.isSafariMobile) {
      window.removeEventListener('resize', this._onResize)
    }

    if (this._onViewportResize && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this._onViewportResize)
    }
    this.bottomSheet?.removeEventListener('keydown', this._onSheetKeyDown)
    document.removeEventListener('focusin', this._onDocumentFocusIn, true)

    this.removeDragListeners()
    if (this._heightUpdateTimeout) {
      clearTimeout(this._heightUpdateTimeout)
    }
    this._observer?.disconnect()
    this._observer = null

    this.bottomSheet = null
    this.sheetOverlay = null
    this.sheetContent = null
    this.dragIcon = null
    this.showBtn = null

    const index = BottomSheet.instances.indexOf(this)
    if (index !== -1) {
      BottomSheet.instances.splice(index, 1)
    }
  }

  /**
   * Updates the content of a specific section of the BottomSheet with the given HTML.
   * @param {string} newHTML - The new HTML to inject into the BottomSheet.
   * @param {string} [target='body'] - The target section ('header', 'body', or 'footer'). Defaults to 'body'.
   */
  updateContent(newHTML, target = 'body') {
    const targetElement = this.bottomSheet.querySelector(`.js-sheet-${target}`)
    if (targetElement) {
      targetElement.innerHTML = newHTML
      this.sheetHeight = this.sheetContent.clientHeight
      this.updateSheetHeight(this.sheetHeight)
    } else {
      console.error(`Error: sheet-${target} element not found. Target provided: ${target}`)
    }
  }
}

// Creates a bottom sheet
// example:
/*
const exampleSheet = createBottomSheet({
  showButton: document.querySelector('#sheet-cta'),
  onShow: (sheet, sheetInstance) => {
    console.log('Sheet has been opened')
  },
  onClose: () => {
    console.log('Sheet has been closed')
  },
})
*/
export const createBottomSheet = config => {
  if (!config) return

  const sheetInstance = new BottomSheet(config)
  if (!sheetInstance) return

  if (config.onShow) {
    sheetInstance.addEventListener('show', bottomSheet => {
      config.onShow(bottomSheet, sheetInstance)
    })
  }

  if (config.onClose) {
    sheetInstance.addEventListener('close', bottomSheet => {
      config.onClose(bottomSheet, sheetInstance)
    })
  }

  return sheetInstance
}

// Creates and shows a bottom sheet with html passed as param in config.html
// example:
// createAndShowHtmlSheet({ html: '<h1>Hello!</h1>' })
const SHEET_TEMPLATE_ID = 'bottom-sheet-template'
export const createAndShowHtmlSheet = config => {
  if (!config || (config && !config.html)) return

  config.sheetClass ??= ''

  const randomId = new Date().getTime()
  const sheetId = `bottom-sheet-${randomId}`
  const sheetTemplate = document.querySelector(`#${SHEET_TEMPLATE_ID}`)

  if (!sheetTemplate) return

  let sheetTemplateHtml = sheetTemplate.innerHTML

  sheetTemplateHtml = sheetTemplateHtml
    .replaceAll('{{sheet_id}}', sheetId)
    .replaceAll('{{sheet_class}}', config.sheetClass)
    .replaceAll('{{sheet_content}}', config.html)

  document.body.insertAdjacentHTML('beforeend', sheetTemplateHtml)

  const sheetHeight = (config.sheetHeight ??= 'auto')
  const sheetInstance = createBottomSheet({ sheetId, sheetHeight })

  sheetInstance.addEventListener('show', bottomSheet => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        config.onShow && config.onShow(bottomSheet, sheetInstance)
        sheetInstance.calcInitialSheetHeight()
      })
    })
  })

  sheetInstance.addEventListener('close', bottomSheet => {
    if (config.onClose) {
      config.onClose(bottomSheet, sheetInstance)
    }

    // timeout to wait until the close animation ends
    setTimeout(() => {
      bottomSheet.remove()
      sheetInstance.destroy()
    }, 500)
  })

  sheetInstance.show(document.activeElement)

  return sheetInstance
}

export default BottomSheet
